import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { customerAdvanceService } from '@/services/customerAdvanceService'
import { financeService } from '@/services/financeService'
import { CustomerAdvanceVm, FinanceCustomer, Account } from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const CustomerAdvancesDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  const [advance, setAdvance] = useState<CustomerAdvanceVm>({
    id: 0,
    customerId: 0,
    debitAccount: 0,
    paymentType: '',
    amount: 0,
    date: calendarToday(),
    refNo: '',
    note: '',
    branchId: branchId || 0,
    ipAddress: '',
  })

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [, setSelectedCustomer] = useState<FinanceCustomer | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Payment type options
  const paymentTypeOptions = [
    { value: 'Bank', label: t('customerAdvances.bank') },
    { value: 'Cash', label: t('customerAdvances.cash') },
  ]

  // Load customers and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, accountsData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          customerAdvanceService.getAllAccounts(branchId),
        ])

        setCustomers(customersData || [])
        setAccounts(accountsData || [])
      } catch (error: any) {
        console.error('Failed to load lookups', error)
        await showError('Error', error.response?.data?.message || t('customerAdvances.failedToLoadData'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load advance data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id || customers.length === 0) return

    const loadAdvance = async () => {
      try {
        setLoading(true)
        const advanceId = Number(id)
        const advanceData = await customerAdvanceService.getAdvanceById(branchId, advanceId)

        // Find customer
        const customer = customers.find(c => c.customerId === advanceData.customerId)
        if (customer) {
          setSelectedCustomer(customer)
        }

        setAdvance({
          id: advanceData.id || 0,
          customerId: advanceData.customerId || 0,
          debitAccount: advanceData.debitAccount || 0,
          paymentType: advanceData.paymentType || '',
          amount: advanceData.amount || 0,
          date: advanceData.date ? calendarFromApi(advanceData.date) || calendarToday() : calendarToday(),
          refNo: advanceData.refNo || '',
          note: advanceData.note || '',
          branchId: branchId,
          ipAddress: advanceData.ipAddress || '',
        })
      } catch (error: any) {
        console.error('Error loading advance', error)
        await showError('Error', error.message || t('customerAdvances.failedToLoadAdvance'))
      } finally {
        setLoading(false)
      }
    }

    loadAdvance()
  }, [isEditMode, branchId, id, customers.length])

  // Handle customer selection
  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.customerId === Number(customerId))
    if (customer) {
      setSelectedCustomer(customer)
      setAdvance(prev => ({
        ...prev,
        customerId: customer.customerId,
      }))
    }
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('customerAdvances.pleaseSelectBranch'))
      return
    }

    if (!advance.customerId || advance.customerId === 0) {
      await showValidationError(t('customerAdvances.pleaseSelectCustomer'))
      return
    }

    if (!advance.debitAccount || advance.debitAccount === 0) {
      await showValidationError(t('customerAdvances.pleaseSelectRecipientAccount'))
      return
    }

    if (!advance.paymentType) {
      await showValidationError(t('customerAdvances.pleaseSelectPaymentType'))
      return
    }

    if (!advance.amount || advance.amount <= 0) {
      await showValidationError(t('customerAdvances.pleaseEnterValidAmount'))
      return
    }

    if (!advance.date) {
      await showValidationError(t('customerAdvances.pleaseSelectDate'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Customer Advance...' : 'Saving Customer Advance...',
        'Please wait while we process your request'
      )

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      const dateISO = calendarToApi(advance.date)

      // Prepare payload matching exact API structure
      const advanceToSave: CustomerAdvanceVm = {
        id: advance.id || 0,
        customerId: advance.customerId,
        debitAccount: advance.debitAccount,
        paymentType: advance.paymentType,
        amount: advance.amount,
        date: dateISO,
        refNo: advance.refNo || '',
        note: advance.note || '',
        branchId: branchId || 0,
        ipAddress: ipAddress, // Set logged-in user's IP address
      }

      // Log the payload to console
      console.log('=== Customer Advance Save Payload ===')
      console.log('Is Edit Mode:', isEditMode)
      console.log('Payload:', JSON.stringify(advanceToSave, null, 2))
      console.log('Payload Object:', advanceToSave)
      console.log('=====================================')

      if (isEditMode) {
        await customerAdvanceService.updateAdvance(advanceToSave)
      } else {
        await customerAdvanceService.insertAdvance(advanceToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('customerAdvances.advanceUpdatedSuccess') : t('customerAdvances.advanceCreatedSuccess')
      )

      navigate('/customer-advances')
    } catch (error: any) {
      console.error('=== Failed to save advance ===')
      console.error('Error:', error)
      console.error('Error Response:', error.response)
      console.error('Error Data:', error.response?.data)
      console.error('Error Status:', error.response?.status)
      console.error('==============================')
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = customers.map((cust) => ({
    value: String(cust.customerId || 0),
    label: cust.customerNameEn || cust.customerNameAr || `Customer ${cust.customerId}`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountCode = acc.accountCode || ''
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
    const label = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(acc.id || acc.accountId || 0),
      label: label,
    }
  })

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('navigation.customerAdvances')} description={isEditMode ? t('customerAdvances.editAdvance') : t('customerAdvances.createNew')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.customerAdvances')}
        description={isEditMode ? t('customerAdvances.editAdvance') : t('customerAdvances.createNew')}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Customer Section */}
            <div className="space-y-2">
              <Label>
                {t('customerAdvances.customer')} <span className="text-destructive">*</span>
              </Label>
              <div className="max-w-[400px]">
                <SearchableSelect
                  options={customerOptions}
                  value={advance.customerId ? String(advance.customerId) : ''}
                  onValueChange={handleCustomerChange}
                  placeholder={t('customerAdvances.selectCustomer')}
                  searchPlaceholder={t('customerAdvances.searchCustomers')}
                  emptyMessage={t('customerAdvances.noCustomersFound')}
                />
              </div>
            </div>

            {/* Advance Info Section */}
            <div className="space-y-4">
              <CardHeader className="px-0 pt-0">
                <CardTitle>{t('customerAdvances.advanceInfo')}</CardTitle>
              </CardHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('customerAdvances.date')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="date"
                      value={advance.date}
                      onChange={(e) => setAdvance(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('customerAdvances.paymentType')}</Label>
                  <div className="max-w-[400px]">
                    <Select
                      value={advance.paymentType}
                      onValueChange={(value) => setAdvance(prev => ({ ...prev, paymentType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('customerAdvances.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent searchable={false}>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('customerAdvances.refNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={advance.refNo || ''}
                      onChange={(e) => setAdvance(prev => ({ ...prev, refNo: e.target.value }))}
                      placeholder={t('customerAdvances.enterReferenceNumber')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('customerAdvances.recipientAccount')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={accountOptions}
                      value={advance.debitAccount ? String(advance.debitAccount) : ''}
                      onValueChange={(value) => setAdvance(prev => ({ ...prev, debitAccount: Number(value) }))}
                      placeholder={t('customerAdvances.selectAccount')}
                      searchPlaceholder={t('customerAdvances.searchAccounts')}
                      emptyMessage={t('customerAdvances.noAccountsFound')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('customerAdvances.amount')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={advance.amount || ''}
                      onChange={(e) => setAdvance(prev => ({ ...prev, amount: Number(e.target.value) || 0 }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label>{t('customerAdvances.note')}</Label>
              <Textarea
                value={advance.note || ''}
                onChange={(e) => setAdvance(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('customerAdvances.enterNotes')}
                rows={4}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/customer-advances')}
          className="w-full sm:w-auto"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('customerAdvances.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('customerAdvances.updateAdvance') : t('customerAdvances.saveAdvance')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

